import { LocationMarkerIcon } from "@heroicons/react/outline";
import produce from "immer";
import { chunk, find, keyBy, values } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import useSWR, { mutate } from "swr";
import CenteredLoading from "../../components/CenteredLoading";
import LoadingIcon from "../../components/icons/LoadingIcon";
import { ApiResponse, fetchApi } from "../../utils/api";
import { Entity } from "../../utils/constants";
import { GeoResult, getDefaultGeoService } from "../../utils/geoservices";
import { convertPlaceResultToGeoResult } from "../../utils/geoservices/geoservice-mapbox";
import { h3ToParentMemoized, kRingMemoized } from "../../utils/memoizedMethods";
import {
  DataById,
  FetchStatus,
  Hotspot,
  HotspotLocation,
  isPlaceResult,
  Placement,
  SuperSimulation
} from "../../utils/types";
import useMapCenter, { Coordinates } from "../../utils/useMapCenter";
import usePrevious from "../../utils/usePrevious";
import useTransmitScaleConfig from "../../utils/useTransmitScaleConfig";
import { useComments } from "../comments";
import { useGoogleMapsWrapper } from "../googleMaps";
import { useLabels } from "../labels";
import { useLineOfSight } from "../lineOfSight";
import { useLoading } from "../loading";
import { LoadingKey } from "../loading/LoadingProvider";
import { usePlacements } from "../placements";
import { PlacementValues } from "../placements/PlacementsProvider";
import { useToast } from "../toast";
import { useWorkspaces } from "../workspaces";
import LocationFormSidebar, {
  EditLocationConfig,
  isHotspotLocation,
  LocationType
} from "./LocationFormSidebar";

interface LocationsMap {
  [locationId: string]: HotspotLocation;
}

type ContextValue = {
  locations: LocationsMap;
  currentLocation: HotspotLocation | undefined;
  locationsFetchStatus: React.MutableRefObject<FetchStatus>;
  locationsAroundCenter: HotspotLocation[];
  addLocation: (
    values: LocationValues
  ) => Promise<ApiResponse<HotspotLocation> | undefined>;
  addLocations: (
    locationValues: LocationValues[],
    labelValues: { name: string; color?: string }[]
  ) => Promise<void>;
  updateLocation: (
    locationId: string,
    values: LocationValues
  ) => Promise<ApiResponse<HotspotLocation>>;
  removeLocations: (
    locationIds: string[]
  ) => Promise<ApiResponse<HotspotLocation[]> | undefined>;
  fetchLocations: () => Promise<LocationsMap>;
  editLocationConfig: EditLocationConfig | undefined;
  setEditLocationConfig: React.Dispatch<
    React.SetStateAction<EditLocationConfig>
  >;
  selectPointsOnMapConfig: SelectPointsOnMapConfig | undefined;
  setSelectPointsOnMapConfig: React.Dispatch<
    React.SetStateAction<SelectPointsOnMapConfig | undefined>
  >;
  renderSelectPointOnMapConfirmationBar: any;
  getLocationCoordinates: (
    uuid: string
  ) => { h3Index: string; lat: number; lng: number } | undefined;
  simulatedLocations: HotspotLocation[];
  setSimulatedLocations: React.Dispatch<
    React.SetStateAction<HotspotLocation[]>
  >;
  findLocationIdByCoordinates: (
    lat: number,
    lng: number
  ) => HotspotLocation | undefined;
  setStartPointLosMapConfig: (
    lat: number,
    lng: number,
    installationHeight?: number
  ) => void;
  abortBulkLocationsRequest: () => Promise<void>;
  currentSuperSimulation: SuperSimulation | undefined;
  superSimulations: DataById<SuperSimulation>;
  locationsLimitExceeded: boolean;
  simulationsLimitExceeded: boolean;
  generateSuperSimulations: (locations: HotspotLocation[]) => Promise<void>;
  fetchLocationDetails: (
    locationUuid: string
  ) => Promise<HotspotLocation | undefined>;
};

const LocationsContext = React.createContext<ContextValue | undefined>(
  undefined
);

interface Props {}

/**
 * This function runs in-place!! Meaning that it mutates the input!
 * It should be deprecated as soon as all metadata is converted to GeoResult
 */
const rewriteLocationMetadata = (location: LocationValues) => {
  const { metadata } = location;
  const parsedMetadata = JSON.parse(metadata ?? "false");

  if (!parsedMetadata || !isPlaceResult(parsedMetadata)) return;

  location.metadata = JSON.stringify(
    convertPlaceResultToGeoResult(parsedMetadata)
  );
};

export const LocationsProvider: React.FC<Props> = ({
  children,
  ...otherProps
}) => {
  const {
    currentWorkspace,
    currentWorkspaceId,
    setWorkspaces,
    currentWorkspaceIsLocked
  } = useWorkspaces();
  const { addLoader, removeLoader, getIsReady } = useLoading();
  const { fetchLabels } = useLabels();
  const { setComments } = useComments();
  const { addToast } = useToast();
  const setActiveIds = useTransmitScaleConfig((state) => state.setActiveIds);
  const fetchedDataCenter = useMapCenter((state) => state.fetchedDataCenter);
  const [locations, setLocations] = useState<LocationsMap>({});
  const locationsFetchStatus = useRef<FetchStatus>("ready");
  const [editLocationConfig, setEditLocationConfig] =
    useState<EditLocationConfig>({ show: false });
  const { status } = useGoogleMapsWrapper();
  const location = useLocation();
  const {
    setStartPoint,
    clearPoints,
    setIsEditingEnd,
    setIsEditingStart,
    setStartHeight,
    setEndHeight,
    endPoint
  } = useLineOfSight();
  const history = useHistory();
  const center = useMapCenter((state) => state.center);
  const setCenter = useMapCenter((state) => state.setCenter);
  const [selectPointsOnMapConfig, setSelectPointsOnMapConfig] = useState<
    SelectPointsOnMapConfig | undefined
  >();
  const [fetchingPlaces, setFetchingPlaces] = useState(false);
  const [placeResult, setPlaceResult] = useState<GeoResult | undefined>();
  const { placements, addPlacement, updatePlacement } = usePlacements();
  const [simulatedLocations, setSimulatedLocations] = useState<
    HotspotLocation[]
  >([]);
  const [controllerBulkLoactions, setControllerBulkLoactions] = useState(
    new AbortController()
  );
  const [signalBulkLoactions, setSignalBulkLoactions] = useState(
    controllerBulkLoactions.signal
  );
  const [loadingBulkLocations, setLoadingBulkLocations] = useState(false);
  const [superSimulations, setSuperSimulations] = useState<
    DataById<SuperSimulation>
  >({});
  const [currentSuperSimulationUuid, setCurrentSuperSimulationUuid] =
    useState<string>("");
  const [currentLocationUuid, setCurrentLocationUuid] = useState<string>("");

  const fetchLocationDetails = useCallback(
    async (locationUuid: string): Promise<HotspotLocation | undefined> => {
      if (!locationUuid || !currentWorkspaceId) return;

      try {
        const response = await fetchApi(
          `/api/workspace/${currentWorkspaceId}/locations/${locationUuid}`
        );

        const apiResponse: ApiResponse<HotspotLocation> = await response.json();

        if (apiResponse.status) {
          setLocations((prevState) =>
            produce(prevState, (draft) => {
              draft[locationUuid] = apiResponse.data;
            })
          );

          setComments((prevState) =>
            produce(prevState, (draft) => {
              draft[locationUuid] = apiResponse.data.comments;
            })
          );
          return apiResponse.data;
        }
      } catch (e) {
        console.error(e);
        return;
      }
    },
    [currentWorkspaceId, setComments]
  );

  const { data: currentLocationData } = useSWR<HotspotLocation | undefined>(
    currentWorkspaceId && currentLocationUuid ? currentLocationUuid : null,
    fetchLocationDetails,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenHidden: false
    }
  );

  const currentLocation = useMemo(
    () =>
      !currentLocationUuid || currentLocationData?.uuid !== currentLocationUuid
        ? undefined
        : currentLocationData,
    [currentLocationData, currentLocationUuid]
  );

  const locationDetailsPageMatch = matchPath<{
    id: string;
    locationUuid: string;
    section: string;
  }>(location.pathname, {
    path: [
      "/locations/:locationUuid/:section",
      "/locations/:locationUuid/super-simulations/:simulationUuid/:subsection"
    ]
  });

  useEffect(() => {
    const newLocationUuid =
      locationDetailsPageMatch?.params?.locationUuid || "";

    if (newLocationUuid !== currentLocationUuid) {
      setCurrentLocationUuid(newLocationUuid);
    }
  }, [locationDetailsPageMatch?.params?.locationUuid, currentLocationUuid]);

  const fetchSuperSimulationDetails = useCallback(
    async (
      superSimulationUuid: string
    ): Promise<SuperSimulation | undefined> => {
      if (!superSimulationUuid || !currentWorkspaceId) return;

      try {
        const response = await fetchApi(
          `/api/workspace/${currentWorkspaceId}/locations-simulation/${superSimulationUuid}`
        );

        const apiResponse: ApiResponse<SuperSimulation> = await response.json();

        if (apiResponse.status) {
          setSuperSimulations((prevState) =>
            produce(prevState, (draft) => {
              draft[superSimulationUuid] = apiResponse.data;
            })
          );
          return apiResponse.data;
        }
      } catch (e) {
        console.error(e);
        return;
      }
    },
    [currentWorkspaceId]
  );

  // TODO: find a way to refetch as long as the result has a status of pending
  const { data: currentSuperSimulationData } = useSWR<
    SuperSimulation | undefined
  >(
    currentSuperSimulationUuid && currentWorkspaceId
      ? currentSuperSimulationUuid
      : null,
    fetchSuperSimulationDetails,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenHidden: false
    }
  );

  const currentSuperSimulation = useMemo(
    () =>
      !currentSuperSimulationUuid ||
      currentSuperSimulationData?.uuid !== currentSuperSimulationUuid
        ? undefined
        : currentSuperSimulationData,
    [currentSuperSimulationData, currentSuperSimulationUuid]
  );

  const superSimulationDetailsPageMatch = matchPath<{
    locationUuid: string;
    simulationUuid: string;
    subsection: string;
  }>(location.pathname, {
    path: "/locations/:locationUuid/super-simulations/:simulationUuid/:subsection"
  });

  useEffect(() => {
    const newSuperSimulationUuid =
      superSimulationDetailsPageMatch?.params?.simulationUuid || "";

    if (newSuperSimulationUuid !== currentSuperSimulationUuid) {
      setCurrentSuperSimulationUuid(newSuperSimulationUuid);
    }
  }, [
    superSimulationDetailsPageMatch?.params?.simulationUuid,
    currentSuperSimulationUuid
  ]);

  const detailsPageMatch = matchPath<{
    tab: string;
  }>(location.pathname, {
    path: `/:tab`
  });

  const locationsLimitExceeded = useMemo(() => {
    if (!currentWorkspace) return false;

    return (
      values(locations).length >=
      currentWorkspace?.active_plan?.allowed_locations
    );
  }, [currentWorkspace, locations]);

  const simulationsLimitExceeded = useMemo(() => {
    if (!currentWorkspace) return false;

    return (
      currentWorkspace?.active_plan?.simulations_used_on_period >=
      currentWorkspace?.active_plan?.allowed_simulations
    );
  }, [currentWorkspace]);

  const getLocationCoordinates = useCallback(
    (uuid: string) => {
      const location = locations[uuid] || find(simulatedLocations, { uuid });
      if (
        !location ||
        !location.h3_index_r12 ||
        !location.lat ||
        !location.lng
      ) {
        return;
      }

      const placement = placements[uuid];
      if (placement) {
        return {
          h3Index: placement.h3_index_r12,
          lat: placement.lat,
          lng: placement.lng
        };
      }

      return {
        h3Index: location.h3_index_r12,
        lat: location.lat,
        lng: location.lng
      };
    },
    [locations, placements, simulatedLocations]
  );

  const fetchPlaces = useCallback(
    async (center: Coordinates) => {
      setFetchingPlaces(true);

      const geoservice = getDefaultGeoService(status.current);
      const results = await geoservice.debouncedSearch(center);

      setFetchingPlaces(false);

      if (!results) return;

      setPlaceResult({ ...results[0], center });
    },
    [status]
  );

  useEffect(() => {
    if (
      !selectPointsOnMapConfig ||
      !!selectPointsOnMapConfig.placementContext ||
      !!selectPointsOnMapConfig.lineOfSightContext
    ) {
      return;
    }

    fetchPlaces(center);
  }, [center, selectPointsOnMapConfig, fetchPlaces]);

  useEffect(() => {
    if (!!selectPointsOnMapConfig) {
      if (
        location.pathname !== "/locations" &&
        !selectPointsOnMapConfig.lineOfSightContext?.pointType
      )
        history.push(`/locations`);

      setTimeout(() => {
        if (selectPointsOnMapConfig.coordinates) {
          setCenter(selectPointsOnMapConfig.coordinates);
        }
      });
    }
  }, [history, location.pathname, selectPointsOnMapConfig, setCenter]);

  const getLocationData: LocationType = useMemo(() => {
    if (!editLocationConfig?.location) {
      return placeResult || center;
    }

    if (isHotspotLocation(editLocationConfig?.location)) {
      return {
        ...editLocationConfig?.location,
        ...(placeResult ? placeResult : {}),
        metadata: placeResult ? JSON.stringify(placeResult) : null,
        name: editLocationConfig?.location.name || placeResult?.name || "",
        lng: placeResult?.center[0] || editLocationConfig?.location.lng,
        lat: placeResult?.center[1] || editLocationConfig?.location.lat
      };
    }

    return placeResult || center;
  }, [center, editLocationConfig?.location, placeResult]);

  const isSavingPlacement = useMemo(
    () => !getIsReady([LoadingKey.savingPlacement]),
    [getIsReady]
  );

  const renderSelectPointOnMapConfirmationBar = useMemo(() => {
    if (!selectPointsOnMapConfig) return null;

    return (
      <div className="absolute bottom-0 inset-x-0 pb-2 sm:pb-5 z-10">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
          <div className="p-2 rounded-lg bg-white dark:bg-gray-800 shadow-lg sm:p-3">
            <div className="flex sm:items-center sm:justify-between flex-col sm:flex-row">
              <div className="flex-1 flex items-center">
                <span className="flex p-2 rounded-lg bg-gray-200 dark:bg-gray-600">
                  {fetchingPlaces ? (
                    <CenteredLoading className="h-6 w-6 text-hotspotty-700 dark:text-white" />
                  ) : (
                    <LocationMarkerIcon
                      className="h-6 w-6 text-hotspotty-700 dark:text-white"
                      aria-hidden="true"
                    />
                  )}
                </span>
                <p className="ml-3 text-gray-900 dark:text-white text-base">
                  {selectPointsOnMapConfig?.placementContext
                    ? "Drag the map to select a placement"
                    : selectPointsOnMapConfig?.lineOfSightContext
                    ? `Select the ${selectPointsOnMapConfig.lineOfSightContext.pointType} point`
                    : placeResult?.name || "Unnamed location"}
                </p>
              </div>
              <div className="flex-shrink-0 flex">
                <button
                  className="mt-3 sm:mt-0 w-full sm:w-auto inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 sm:text-sm dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400"
                  type="button"
                  onClick={() => {
                    if (selectPointsOnMapConfig?.placementContext?.redirectTo) {
                      history.push(
                        selectPointsOnMapConfig.placementContext.redirectTo
                      );
                    } else if (selectPointsOnMapConfig.lineOfSightContext) {
                      clearPoints();
                    }
                    setSelectPointsOnMapConfig(undefined);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="ml-2 mt-3 sm:mt-0 w-full sm:w-auto inline-flex justify-center rounded-md shadow-sm px-4 py-2 text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 sm:text-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 disabled:opacity-50 disabled:cursor-default"
                  type="button"
                  disabled={fetchingPlaces || isSavingPlacement}
                  onClick={async () => {
                    if (selectPointsOnMapConfig?.placementContext) {
                      const { hotspot, location, placement, redirectTo } =
                        selectPointsOnMapConfig.placementContext;

                      const entity = location
                        ? Entity.location
                        : Entity.hotspot;

                      const id = location
                        ? location.uuid
                        : (hotspot as Hotspot).id;

                      const placementValues: PlacementValues = {
                        placementable_id: id,
                        placementable_type: entity,
                        lat: center[1],
                        lng: center[0]
                      };

                      if (placement) {
                        updatePlacement(placement.uuid, placementValues).then(
                          () => {
                            setSelectPointsOnMapConfig(undefined);
                            history.push(redirectTo);
                          }
                        );
                      } else {
                        addPlacement(placementValues).then(() => {
                          setSelectPointsOnMapConfig(undefined);
                          history.push(redirectTo);
                        });
                      }
                    } else if (selectPointsOnMapConfig.lineOfSightContext) {
                      const { lineOfSightContext } = selectPointsOnMapConfig;

                      if (
                        lineOfSightContext.pointType === "start" &&
                        !endPoint
                      ) {
                        setStartPoint(center);
                        setIsEditingStart(false);
                        setIsEditingEnd(true);
                        setSelectPointsOnMapConfig({
                          coordinates: endPoint,
                          lineOfSightContext: {
                            pointType: "end"
                          }
                        });
                      }

                      if (
                        lineOfSightContext.pointType === "start" &&
                        endPoint
                      ) {
                        setStartPoint(center);
                        setIsEditingStart(false);
                        setSelectPointsOnMapConfig(undefined);
                        history.replace(
                          `/${detailsPageMatch?.params.tab}/line-of-sight`
                        );
                      }

                      if (lineOfSightContext.pointType === "end") {
                        setIsEditingEnd(false);
                        setSelectPointsOnMapConfig(undefined);
                        history.replace(
                          `/${detailsPageMatch?.params.tab}/line-of-sight`
                        );
                      }
                    } else {
                      setEditLocationConfig({
                        show: true,
                        location: getLocationData,
                        onSuccess: (location) =>
                          isHotspotLocation(location) &&
                          history.push(
                            `/locations/${location.uuid}/super-simulations`
                          )
                      });
                      setSelectPointsOnMapConfig(undefined);
                    }
                  }}
                >
                  {isSavingPlacement && (
                    <LoadingIcon className="animate-spin -ml-1 mr-3 h-5 w-5" />
                  )}
                  <span>
                    {isSavingPlacement ? "Saving placement" : "Confirm"}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }, [
    selectPointsOnMapConfig,
    fetchingPlaces,
    placeResult?.name,
    isSavingPlacement,
    history,
    clearPoints,
    center,
    updatePlacement,
    addPlacement,
    setStartPoint,
    setIsEditingStart,
    setIsEditingEnd,
    endPoint,
    detailsPageMatch?.params.tab,
    getLocationData
  ]);

  const locationsAroundCenter = useMemo(() => {
    // TODO: get this from the backend
    const filteredLocations = values(locations);

    // fetchedDataCenter of the fetched data which defines the center of the purple zone on the map
    if (!fetchedDataCenter) return filteredLocations;

    return filteredLocations.filter((location) =>
      kRingMemoized(fetchedDataCenter, 2).includes(
        h3ToParentMemoized(location.h3_index_r12, 4)
      )
    );
  }, [fetchedDataCenter, locations]);

  const fetchLocations = useCallback(async () => {
    if (!currentWorkspaceId) return {};

    addLoader(LoadingKey.workspaceLocations);

    locationsFetchStatus.current = "fetching";
    const res = await fetchApi(
      `/api/workspace/${currentWorkspaceId}/locations`
    );

    const apiResponse: ApiResponse<HotspotLocation[]> = await res.json();

    let newLocations = {};

    if (apiResponse.status) {
      const convertedLocations = apiResponse.data.map((location) => {
        rewriteLocationMetadata(location);
        return location;
      });
      newLocations = keyBy(convertedLocations, "uuid");

      setLocations(newLocations);
    }
    locationsFetchStatus.current = "ready";

    removeLoader(LoadingKey.workspaceLocations);

    return newLocations;
  }, [addLoader, currentWorkspaceId, removeLoader]);

  const prevWorkspaceId = usePrevious(currentWorkspaceId);
  useEffect(() => {
    if (
      currentWorkspaceId &&
      prevWorkspaceId &&
      currentWorkspaceId !== prevWorkspaceId
    ) {
      currentLocationUuid && mutate(currentLocationUuid);
      currentSuperSimulationUuid && mutate(currentSuperSimulationUuid);
      setLocations({});
      setSuperSimulations({});
    }
  }, [
    currentLocationUuid,
    currentSuperSimulationUuid,
    currentWorkspaceId,
    prevWorkspaceId
  ]);

  useEffect(() => {
    if (currentWorkspaceIsLocked) return;

    fetchLocations();
  }, [currentWorkspaceIsLocked, fetchLocations]);

  const addLocation = useCallback(
    async (values: LocationValues) => {
      if (!currentWorkspaceId) return;

      rewriteLocationMetadata(values);
      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/locations`,
        {
          method: "POST",
          body: JSON.stringify(values)
        }
      );

      const apiResponse: ApiResponse<HotspotLocation> = await response.json();

      if (apiResponse.status) {
        setWorkspaces((prevState) =>
          produce(prevState, (draft) => {
            if (!draft[currentWorkspaceId].counts) return;

            draft[currentWorkspaceId].counts.location += 1;
          })
        );

        await fetchLocations();
      }

      return apiResponse;
    },
    [currentWorkspaceId, fetchLocations, setWorkspaces]
  );

  const addLocations = useCallback(
    async (
      locationValues: LocationValues[],
      labelValues: { name: string; color?: string }[]
    ) => {
      if (!currentWorkspaceId) return;

      try {
        setLoadingBulkLocations(true);

        locationValues.forEach((location) => rewriteLocationMetadata(location));
        const locationChunks = chunk(locationValues, 50);

        for (const chunkedLocationValues of locationChunks) {
          await fetchApi(
            `/api/workspace/${currentWorkspaceId}/locations/bulk`,
            {
              method: "POST",
              signal: signalBulkLoactions,
              body: JSON.stringify({
                locations: chunkedLocationValues,
                labels: labelValues
              })
            }
          );
        }

        await Promise.all([fetchLocations(), fetchLabels()]);

        setWorkspaces((prevState) =>
          produce(prevState, (draft) => {
            if (!draft[currentWorkspaceId].counts) return;

            draft[currentWorkspaceId].counts.location += locationValues.length;
          })
        );

        setLoadingBulkLocations(false);
      } catch (error) {
        setLoadingBulkLocations(false);
      }
    },
    [
      fetchLocations,
      fetchLabels,
      setWorkspaces,
      currentWorkspaceId,
      signalBulkLoactions
    ]
  );

  const updateLocation = useCallback(
    async (locationId: string, values: LocationValues) => {
      rewriteLocationMetadata(values);
      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/locations/${locationId}`,
        {
          method: "PUT",
          body: JSON.stringify(values)
        }
      );

      const apiResponse: ApiResponse<HotspotLocation> = await response.json();

      if (apiResponse.status) {
        await fetchLocations();
      }

      return apiResponse;
    },
    [currentWorkspaceId, fetchLocations]
  );

  const removeLocations = useCallback(
    async (locationIds: string[]) => {
      if (!currentWorkspaceId) return;

      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/locations`,
        {
          method: "DELETE",
          body: JSON.stringify({ locations: locationIds })
        }
      );

      const apiResponse: ApiResponse<[]> = await response.json();

      if (apiResponse.status) {
        await fetchLocations();

        setWorkspaces((prevState) =>
          produce(prevState, (draft) => {
            if (!draft[currentWorkspaceId].counts) return;

            draft[currentWorkspaceId].counts.location -= locationIds.length;
          })
        );

        setActiveIds(locationIds);
      } else {
        addToast({
          type: "error",
          message: apiResponse.message || "Failed to remove locations"
        });
      }

      return apiResponse;
    },
    [addToast, currentWorkspaceId, fetchLocations, setActiveIds, setWorkspaces]
  );

  const findLocationIdByCoordinates = useCallback(
    (lat: number, lng: number) =>
      find(values(locations), {
        lat: roundToDecimals(lat, 5),
        lng: roundToDecimals(lng, 5)
      }),
    [locations]
  );

  const setStartPointLosMapConfig = useCallback(
    (lat: number, lng: number, installationHeight?: number) => {
      clearPoints();
      setSelectPointsOnMapConfig(undefined);
      if (installationHeight) {
        setStartHeight(installationHeight);
        setEndHeight(installationHeight);
      }
      setStartPoint([lng, lat]);
      setIsEditingStart(false);
      setIsEditingEnd(true);
      setSelectPointsOnMapConfig({
        coordinates: undefined,
        lineOfSightContext: {
          pointType: "end"
        }
      });
    },
    [
      clearPoints,
      setEndHeight,
      setIsEditingEnd,
      setIsEditingStart,
      setStartHeight,
      setStartPoint
    ]
  );

  const abortBulkLocationsRequest = useCallback(async () => {
    controllerBulkLoactions.abort();

    const newAbortController = new AbortController();

    setControllerBulkLoactions(newAbortController);
    setSignalBulkLoactions(newAbortController.signal);

    if (!loadingBulkLocations) return;

    await Promise.all([fetchLocations(), fetchLabels()]);
  }, [
    controllerBulkLoactions,
    fetchLabels,
    fetchLocations,
    loadingBulkLocations
  ]);

  const generateSuperSimulations = useCallback(
    async (locations: HotspotLocation[]) => {
      if (!currentWorkspaceId) return;

      try {
        const response = await fetchApi(
          `/api/workspace/${currentWorkspaceId}/locations-simulations/bulk`,
          {
            method: "POST",
            body: JSON.stringify({
              locations: locations.map((location) => ({
                location_uuid: location.uuid,
                height: location.height
              }))
            })
          }
        );

        const apiResponse: ApiResponse<SuperSimulation[]> =
          await response.json();

        if (apiResponse.status) {
          setWorkspaces((prevState) =>
            produce(prevState, (draft) => {
              if (!draft[currentWorkspaceId].active_plan) return;

              draft[
                currentWorkspaceId
              ].active_plan.simulations_used_on_period += locations.length;
            })
          );

          await fetchLocations();
        } else {
          addToast({
            type: "error",
            message: apiResponse.message || "Failed to generate simulations"
          });
        }
      } catch (e) {
        console.error(e);

        addToast({
          type: "error",
          message: "Failed to generate simulations"
        });
      }
    },
    [addToast, currentWorkspaceId, fetchLocations, setWorkspaces]
  );

  const value = useMemo(
    () => ({
      locations,
      currentLocation,
      locationsAroundCenter,
      locationsFetchStatus,
      fetchLocations,
      addLocation,
      addLocations,
      updateLocation,
      removeLocations,
      editLocationConfig,
      setEditLocationConfig,
      selectPointsOnMapConfig,
      setSelectPointsOnMapConfig,
      renderSelectPointOnMapConfirmationBar,
      getLocationCoordinates,
      simulatedLocations,
      setSimulatedLocations,
      findLocationIdByCoordinates,
      setStartPointLosMapConfig,
      abortBulkLocationsRequest,
      currentSuperSimulation,
      superSimulations,
      locationsLimitExceeded,
      generateSuperSimulations,
      simulationsLimitExceeded,
      fetchLocationDetails
    }),
    [
      locations,
      currentLocation,
      locationsAroundCenter,
      fetchLocations,
      addLocation,
      addLocations,
      updateLocation,
      removeLocations,
      editLocationConfig,
      selectPointsOnMapConfig,
      renderSelectPointOnMapConfirmationBar,
      getLocationCoordinates,
      simulatedLocations,
      findLocationIdByCoordinates,
      setStartPointLosMapConfig,
      abortBulkLocationsRequest,
      currentSuperSimulation,
      superSimulations,
      locationsLimitExceeded,
      generateSuperSimulations,
      simulationsLimitExceeded,
      fetchLocationDetails
    ]
  );

  return (
    <LocationsContext.Provider value={value} {...otherProps}>
      <LocationFormSidebar
        config={editLocationConfig}
        setConfig={setEditLocationConfig}
      />
      {children}
    </LocationsContext.Provider>
  );
};

export const useLocations = (): ContextValue => {
  const context = useContext(LocationsContext);
  if (context === undefined) {
    throw new Error("useLocations must be used within an LocationsProvider");
  }
  return context;
};

//
// Utils
//

export interface LocationValues {
  name: string;
  address?: string | null;
  lat: number;
  lng: number;
  height: number;
  metadata?: string | null;
}

export interface SelectPointsOnMapConfig {
  coordinates: Coordinates | undefined;
  lineOfSightContext?: {
    pointType: "start" | "end";
  };
  placementContext?: {
    location: HotspotLocation | undefined;
    hotspot: Hotspot | undefined;
    placement: Placement | undefined;
    redirectTo: string;
  };
  origin?: "lineOfSight" | "placement" | null;
}

const roundToDecimals = (number: number, decimalsToRound: number) =>
  Math.round(number * Math.pow(10, decimalsToRound)) /
  Math.pow(10, decimalsToRound);
